import authService from '../utils/authService';
import config from '../config';

class SupportApiService {
  constructor() {
    this.baseUrl = `${config.API_BASE_URL}/api/support/dashboard`;
    
    // Explicitly bind all methods to ensure 'this' context is preserved
    this.makeApiCall = this.makeApiCall.bind(this);
    this.getDashboard = this.getDashboard.bind(this);
    this.getSearchHotel = this.getSearchHotel.bind(this);
    this.getAccountManager = this.getAccountManager.bind(this);
    this.getAccountManagerDetails = this.getAccountManagerDetails.bind(this);
    this.approveAgent = this.approveAgent.bind(this);
    this.disapproveAgent = this.disapproveAgent.bind(this);
    this.searchHotels = this.searchHotels.bind(this);
    this.getHotelDetails = this.getHotelDetails.bind(this);
    this.getHotelImages = this.getHotelImages.bind(this);
    this.getAllSupportTickets = this.getAllSupportTickets.bind(this);
    this.assignTicket = this.assignTicket.bind(this);
    this.getTokenRecharge = this.getTokenRecharge.bind(this);
    this.getTokenRechargeOperation = this.getTokenRechargeOperation.bind(this);
    this.processTokenRecharge = this.processTokenRecharge.bind(this);
    this.getTokenRechargeHistory = this.getTokenRechargeHistory.bind(this);
    this.getBookingDetails = this.getBookingDetails.bind(this);
    this.getCancellationPageDetails = this.getCancellationPageDetails.bind(this);
    this.getCancellationDetailsById = this.getCancellationDetailsById.bind(this);
    this.getPaymentInfo = this.getPaymentInfo.bind(this);
    this.getManualPaymentInfo = this.getManualPaymentInfo.bind(this);
    this.updateTransactionStatus = this.updateTransactionStatus.bind(this);
    this.getRefundInfo = this.getRefundInfo.bind(this);
    this.getSupportTickets = this.getSupportTickets.bind(this);
    this.getTicketDetails = this.getTicketDetails.bind(this);
    this.addCommentToTicket = this.addCommentToTicket.bind(this);
    
    console.log('🎧 SupportApiService initialized');
  }

  // Helper method to make authenticated API calls
  async makeApiCall(endpoint, options = {}) {
    const defaultOptions = {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...authService.getAuthHeader()
      },
      credentials: 'include',
      ...options
    };

    try {
      const response = await fetch(`${this.baseUrl}${endpoint}`, defaultOptions);
      
      if (response.status === 401) {
        // Token expired or invalid
        authService.logout();
        window.location.href = '/login';
        throw new Error('Authentication expired');
      }

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      return data;
    } catch (error) {
      console.error(`Support API call failed for ${endpoint}:`, error);
      throw error;
    }
  }

  // Dashboard APIs
  async getDashboard() {
    return this.makeApiCall('/overview');
  }

  // Update password for force-change flow (support first login)
  async updatePassword(newPassword) {
    return fetch(`${config.API_BASE_URL}/api/auth/update-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...authService.getAuthHeader()
      },
      credentials: 'include',
      body: JSON.stringify({ newPassword })
    }).then(res => res.json());
  }

  async getSearchHotel() {
    return this.makeApiCall('/searchhotel');
  }

  // Account Manager APIs
  async getAccountManager() {
    return this.makeApiCall('/account-manager');
  }

  async getAccountManagerDetails(id) {
    return this.makeApiCall(`/account-manager/details/${id}`);
  }

  async approveAgent(id) {
    return this.makeApiCall(`/account-manager/approve/${id}`, {
      method: 'POST'
    });
  }

  async disapproveAgent(id) {
    return this.makeApiCall(`/account-manager/disapprove/${id}`, {
      method: 'POST'
    });
  }

  // Support Tickets APIs
  async getAllSupportTickets(page = 0, size = 5) {
    return this.makeApiCall(`/all-tickets?page=${page}&size=${size}`);
  }

  async assignTicket(ticketId) {
    return this.makeApiCall(`/assign-ticket/${ticketId}`, {
      method: 'POST'
    });
  }

  // Token Recharge APIs
  async getTokenRecharge() {
    return this.makeApiCall('/token-recharge');
  }

  async getTokenRechargeOperation(agentId) {
    return this.makeApiCall(`/token-recharge-page/recharge/${agentId}`);
  }

  async processTokenRecharge(agentId, tokens, amount) {
    const formData = new URLSearchParams();
    formData.append('tokens', tokens);
    if (amount) formData.append('amount', amount);

    return this.makeApiCall(`/redirectToTokenRecharge/${agentId}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        ...authService.getAuthHeader()
      },
      body: formData.toString()
    });
  }

  async getTokenRechargeHistory(agentId) {
    return this.makeApiCall(`/token-recharge/token-recharge-history/${agentId}`);
  }

  // Hotel Bookings APIs
  async getBookingDetails() {
    return this.makeApiCall('/booking-details');
  }

  async getBookingDetailsById(bookingId) {
    return this.makeApiCall(`/support/booking/details/${bookingId}`);
  }

  async cancelBooking(bookingId) {
    return this.makeApiCall(`/support/booking/cancel/${bookingId}`, {
      method: 'POST'
    });
  }

  async saveHotelConfirmationNo(bookingId, hotelConfirmationNo) {
    const formData = new URLSearchParams();
    formData.append('bookingId', bookingId);
    formData.append('hotelConfirmationNo', hotelConfirmationNo);

    return this.makeApiCall('/booking-details/submit-hotel-confirmation-no', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        ...authService.getAuthHeader()
      },
      body: formData.toString()
    });
  }

  // Booking Cancellation APIs
  async getCancellationPageDetails() {
    return this.makeApiCall('/cancellation-page-details');
  }

  async getCancellationDetailsById(cancellationId) {
    return this.makeApiCall(`/support/cancellation/details/${cancellationId}`);
  }

  // Payment Info APIs
  async getPaymentInfo() {
    return this.makeApiCall('/payment-info');
  }

  // Manual Payment APIs
  async getManualPaymentInfo() {
    return this.makeApiCall('/manual-payment-info');
  }

  async getManualPaymentByTicketId(ticketId) {
    return this.makeApiCall(`/manual-payment-info/${ticketId}`);
  }

  async updateTransactionStatus(agentId, transactionId, amount, status) {
    return this.makeApiCall('/update-transaction-status', {
      method: 'POST',
      body: JSON.stringify({
        agentId,
        transactionId,
        amount,
        status
      })
    });
  }

  // Refund Info APIs
  async getRefundInfo() {
    return this.makeApiCall('/refund-info');
  }

  // Support Tickets APIs
  async getSupportTickets(statuses = null) {
    const statusParam = statuses && statuses.length > 0 
      ? '?' + statuses.map(status => `status=${encodeURIComponent(status)}`).join('&')
      : '';
    return this.makeApiCall(`/support-tickets${statusParam}`);
  }

  async getTicketDetails(ticketId) {
    return this.makeApiCall(`/support-tickets/${ticketId}`);
  }

  async addCommentToTicket(ticketId, comment) {
    return this.makeApiCall(`/support-tickets/${ticketId}/add-comment`, {
      method: 'POST',
      body: JSON.stringify({ comment })
    });
  }

  // POST APIs for actions
  async searchHotels(searchData) {
    return this.makeApiCall('/searchhotel', {
      method: 'POST',
      body: JSON.stringify(searchData)
    });
  }

  // Hotel Details APIs (shared with Agent)
  async getHotelDetails(hotelId, provider, searchId) {
    const endpoint = `/api/hotel-details/${hotelId}?provider=${encodeURIComponent(provider)}&searchId=${encodeURIComponent(searchId)}`;
    
    try {
      const response = await fetch(`${config.API_BASE_URL}${endpoint}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...authService.getAuthHeader()
        },
        credentials: 'include'
      });

      if (response.status === 401) {
        authService.logout();
        window.location.href = '/login';
        throw new Error('Authentication expired');
      }

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      return data;
    } catch (error) {
      console.error(`Hotel details API call failed:`, error);
      throw error;
    }
  }

  async getHotelImages(hotelId, provider) {
    const endpoint = `/api/hotel-details/${hotelId}/images?provider=${encodeURIComponent(provider)}`;
    
    try {
      const response = await fetch(`${config.API_BASE_URL}${endpoint}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...authService.getAuthHeader()
        },
        credentials: 'include'
      });

      if (response.status === 401) {
        authService.logout();
        window.location.href = '/login';
        throw new Error('Authentication expired');
      }

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      return data;
    } catch (error) {
      console.error(`Hotel images API call failed:`, error);
      throw error;
    }
  }

  // Virtual Account APIs
  async getVirtualAccountPage() {
    return this.makeApiCall('/virtual-account');
  }

  async searchAgents(searchTerm = '', page = 0, size = 10) {
    const params = new URLSearchParams({
      page: page.toString(),
      size: size.toString()
    });
    
    if (searchTerm && searchTerm.trim()) {
      params.append('searchTerm', searchTerm.trim());
    }
    
    return this.makeApiCall(`/search-agents?${params.toString()}`);
  }

  async getAgentByIdentifier(identifier) {
    return this.makeApiCall(`/agent?identifier=${encodeURIComponent(identifier)}`);
  }

  async findWallet(identifier) {
    return this.makeApiCall(`/wallet/find?identifier=${encodeURIComponent(identifier)}`);
  }

  async addAmountToWallet(identifier, amount) {
    return this.makeApiCall('/wallet/add-amount', {
      method: 'POST',
      body: JSON.stringify({ identifier, amount })
    });
  }

  async createVirtualAccount(virtualAccountData) {
    return this.makeApiCall('/virtual-account', {
      method: 'POST',
      body: JSON.stringify(virtualAccountData)
    });
  }
}

// Create the service instance
const supportApiServiceInstance = new SupportApiService();

export default supportApiServiceInstance;